import { Flex, Text } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";

import { RanaModal } from "@nens/ranalib";
import { FormErrors, RanaForm, SubmitFunction } from "@nens/ranalib";

import { cancelButtonText } from "@/translations";

import { getFiles } from "../../../store/selectors";
import { usePublicationStore } from "../../../store/store";
import { CompleteFile } from "../../../types/file-reference-with-file";

interface PublicationDetailFileReferenceNameModalProps {
  fileReference: CompleteFile;
  onClose: () => void;
}

type PublicationFileRefrenceFormValues = {
  name: string;
};

export function PublicationDetailFileReferenceNameModal({
  fileReference,
  onClose,
}: PublicationDetailFileReferenceNameModalProps) {
  const intl = useIntl();
  const title = intl.formatMessage({
    id: "reference.change_file_name",
    defaultMessage: "Rename file",
  });

  const publicationFileReferences = usePublicationStore(getFiles);
  const changeFileName = usePublicationStore((state) => state.changeFileName);

  const INITIAL_VALUES: PublicationFileRefrenceFormValues = {
    name: fileReference.name + "",
  };

  const validate = (values: PublicationFileRefrenceFormValues) => {
    const errors: FormErrors = {};
    if (publicationFileReferences.find(({ name }) => name === values.name))
      errors.name = intl.formatMessage({
        id: "form.file.reference.exists",
        defaultMessage: "Already a file with that reference name",
      });

    return errors;
  };

  // Submit can assume validate returned no errors.
  const submit: SubmitFunction<PublicationFileRefrenceFormValues> = async ({ name }) => {
    const updatedFile: CompleteFile = {
      ...fileReference,
      name: name,
    };
    changeFileName(updatedFile);
    onClose();
  };

  return (
    <RanaForm initialValues={INITIAL_VALUES} validate={validate} doSubmit={submit}>
      <RanaModal
        isOpen
        onClose={onClose}
        topBar={title}
        maxW="xl"
        buttons={[
          {
            children: cancelButtonText,
            variant: "tertiary",
            onClick: onClose,
          },
          <RanaForm.SubmitButton key={title}>{title}</RanaForm.SubmitButton>,
        ]}
      >
        <Flex flexDir="column" gap={2}>
          <Text>
            <FormattedMessage
              id="publication.file.name.warning"
              defaultMessage="IMPORTANT: This does not change the name of the actual file. Just the name of the reference to the file."
            />
          </Text>
          <RanaForm.Input isRequired submitOnEnter field="name" label="Name" />
          <RanaForm.Errors />
        </Flex>
      </RanaModal>
    </RanaForm>
  );
}
